'use client'

/**
 * Render whichever row the entry's kind supplies.
 *
 * This is what the seam buys, and it is the reason `registry.ts` exists: the feed used to carry a
 * seven-arm `switch` on `entry.type`, each arm naming one row component and repeating the same
 * seven props, with a cast per arm because `EntryWithPeople.type` is `EntryType | string`. It is
 * one lookup now, and adding a kind is a key in the registry rather than an eighth arm nobody
 * remembers to write.
 *
 * **The one cast lives here.** `AnySlice['row']` is a union of eight component types, so TypeScript
 * cannot correlate the row it picked with the entry it picked it for — a heterogeneous dispatch
 * costs exactly one assertion, and this is it, in place of the seven it replaces. `sliceFor` has
 * already done the narrow that matters; what remains is variance, not a widened field.
 */

import type { ComponentType } from 'react'

import type { EntryWithPeople } from '@/document'

import { sliceFor } from './registry'
import type { RowContext } from './types'

export interface FeedRowProps {
  entry: EntryWithPeople
  isExpanded: boolean
  onToggle: () => void
  onDelete: () => void
  onSave: (draft: never) => Promise<void>
  isDeleting: boolean
  isSaving: boolean
  context?: RowContext
  hideTopics?: boolean
}

/**
 * Returns `null` for a kind with no row — `person`, and any kind no slice covers, which the data
 * really does hold (`'album'`, #395). Both were silent fall-throughs of the old `switch`; here the
 * absence is a value the registry states.
 */
export function FeedRow(props: FeedRowProps) {
  const Row = sliceFor(props.entry)?.row
  if (!Row) return null

  const Typed = Row as ComponentType<FeedRowProps>
  return <Typed {...props} />
}
